import { useEffect, useState } from 'react'
import { getLocalAccessToken, setLocalAccessToken } from 'helper/common'
import { fetchUser, LogOutAction, setUserToken } from 'redux/store/user/slice'
import { useSelector } from 'react-redux'
import { ability, updateAbility } from 'features/Permission'
import { getToken, getUser } from 'redux/store/user/getters'
import { setLoading } from 'redux/store/common/slice'
import { routes } from 'router/Config/config.routes'
import useRouter from 'hooks/useRouter'
import { DEFAULT_PUBLIC_URLS, PUBLIC_URLS } from './constants'
import { fetchCommunityInfo } from 'redux/store/chapterInfo/slice'
import { fetchVendorInfo } from 'redux/store/vendorInfo/slice'
import { TAuthProps } from './types'
import { getVendorInfo } from '../../redux/store/vendorInfo/getters'
import { getAllowedRolesList } from '../../redux/store/myCommunity/actions'
import { useBottomNotificationsContext } from '../BottomNotificationProvider/BottomNotificationsContextProvider'
import { createSearchParams } from 'react-router-dom'
import { useAppDispatch } from '../../redux/createStore'
import { useDescope } from '@descope/react-sdk'

export const AuthCheck = ({ children }: TAuthProps): JSX.Element | null => {
  const dispatch = useAppDispatch()
  const { push, pathname, query } = useRouter()
  const { onSessionTokenChange } = useDescope()
  const accessToken = getLocalAccessToken()
  const user = useSelector(getUser)
  const token = useSelector(getToken)
  const [_isUserInitialized, _setIsUserInitialized] = useState(false)

  const {
    actions: { destroyAllNotifications }
  } = useBottomNotificationsContext()

  // update token in store when it got automatically refreshed or granted
  useEffect(() => {
    const unsubscribeTokenChange = onSessionTokenChange((token) => {
      setLocalAccessToken(token)
      dispatch(setUserToken({ token }))
    })
    return () => {
      unsubscribeTokenChange()
    }
  }, [dispatch, onSessionTokenChange])

  useEffect(() => {
    dispatch(setLoading(true))
  }, [dispatch])

  useEffect(() => {
    if ((token || accessToken) && !user?.uuid) {
      dispatch(setLoading(true))
      dispatch(
        fetchUser(
          (data) => {
            if (
              data?.role.includes('community') &&
              data?.community &&
              !data?.outside_community
            ) {
              dispatch(fetchCommunityInfo(data?.community))
            }
            if (data?.role.includes('vendor') && data?.vendor) {
              dispatch(fetchVendorInfo(data?.vendor))
            }
            dispatch(getAllowedRolesList())

            if (token) push(routes.portal)
            _setIsUserInitialized(true)
          },
          (status) => {
            _setIsUserInitialized(true)
            if (status === 403) {
              push(routes.userNotExist)
            }
          }
        )
      )
    } else {
      _setIsUserInitialized(true)
    }
  }, [accessToken, dispatch, push, token, user?.uuid])

  const vendorInfo = useSelector(getVendorInfo)

  useEffect(() => {
    if (!accessToken && pathname === '/') {
      dispatch(setLoading(false))
      push({
        pathname: routes.landing,
        search: createSearchParams(query ?? {}).toString()
      })
      return
    }
    if (!PUBLIC_URLS.find((i) => i === pathname)) {
      if (!accessToken) {
        destroyAllNotifications()
        dispatch(LogOutAction())
        if (!pathname.includes('login')) push(routes.login)
        dispatch(setLoading(false))
        return
      }
      updateAbility(ability, user, vendorInfo)
    }
    if (DEFAULT_PUBLIC_URLS.find((i) => i === pathname)) {
      dispatch(setLoading(false))
    }
  }, [
    accessToken,
    dispatch,
    pathname,
    push,
    user,
    user.uuid,
    vendorInfo,
    destroyAllNotifications,
    query
  ])

  return _isUserInitialized ? (children as JSX.Element) : null
}
